import { useMemo } from "react";
import { useSelector } from "react-redux";

import { resolveId } from "@medplum/core";
import { Encounter } from "@medplum/fhirtypes";

import { isBefore, parseISO, startOfDay } from "date-fns";

import {
  calculateGestationalAgeInWeeksAndDays,
  Gestation,
  postpartumWeeksAndDaysAt,
  PregnancyRecord,
  selectPregnancyRecordForEncounterDate,
} from "@/pregnancy";
import { calculateEDDFromLastMenstrualPeriod } from "@/pregnancy/utils/edd";
import {
  selectAllPatientPregnancyRecords,
  selectMostRecentPastPregnancyRecordForEncounter,
  selectPregnancyRecordForEncounter,
} from "@/store/slices/pregnancy";

/**
 * Returns whether the encounter service date is on or after the pregnancy close-out
 * (delivery) date, compared at calendar-day granularity.
 *
 * The delivery day itself is included (postpartum day 0), so an encounter whose service
 * date equals the delivery date returns `true`.
 */
function isEncounterAfterPregnancyEnd(
  encounterDateISO: string,
  endDateTimeISO: string | undefined,
): boolean {
  if (!endDateTimeISO || !encounterDateISO) return false;

  return !isBefore(startOfDay(parseISO(encounterDateISO)), startOfDay(parseISO(endDateTimeISO)));
}

/**
 * When a linked pregnancy is retired, the encounter may still be on the List but
 * occur after delivery. In that case GA from EDD would exceed term and should
 * not be shown; postpartum age applies instead.
 *
 * The delivery anchor is `deliveryDate` when present (updated by GTPALDrawer and may
 * differ from the administrative close-out date), falling back to `endDateTime`.
 */
function shouldUsePostpartumAgeForLinkedRecord(
  pregnancyRecord: PregnancyRecord,
  encounterDateISO: string,
): boolean {
  if (pregnancyRecord.isActive || !encounterDateISO) return false;

  // Prefer the actual delivery date (set by GTPALDrawer) over the close-out date so that
  // an encounter between the real delivery and the administrative close-out is still treated
  // as postpartum.
  const deliveryAnchor = pregnancyRecord.deliveryDate ?? pregnancyRecord.endDateTime;
  if (isEncounterAfterPregnancyEnd(encounterDateISO, deliveryAnchor)) {
    return true;
  }

  const edd = resolvePregnancyRecordEdd(pregnancyRecord);
  if (!pregnancyRecord.endDateTime || !edd || !pregnancyRecord.gestationalAge) {
    return false;
  }

  const gestationAtEncounter = calculateGestationalAgeInWeeksAndDays(edd, encounterDateISO);
  const encounterTotalDays = gestationAtEncounter.weeks * 7 + gestationAtEncounter.days;
  const closeoutTotalDays =
    pregnancyRecord.gestationalAge.weeks * 7 + pregnancyRecord.gestationalAge.days;

  return encounterTotalDays > closeoutTotalDays;
}

/**
 * Resolves the EDD used for gestational-age display. Prefers a stored EDD; when
 * missing (e.g. cancelled after close-out and not restored on the record), derives
 * EDD from LMP so prenatal Weeks remain available on retired pregnancies.
 *
 * @param pregnancyRecord - Linked pregnancy record
 * @returns ISO EDD string, or `undefined` when neither EDD nor LMP is available
 */
function resolvePregnancyRecordEdd(pregnancyRecord: PregnancyRecord): string | undefined {
  if (pregnancyRecord.edd) return pregnancyRecord.edd;
  if (!pregnancyRecord.lastMenstrualPeriod) return undefined;
  return calculateEDDFromLastMenstrualPeriod(pregnancyRecord.lastMenstrualPeriod);
}

/**
 * Gestational age for a pregnancy record at an encounter service date, or
 * `undefined` when postpartum age should be shown instead / inputs are missing.
 */
function gestationalAgeForPregnancyRecord(
  pregnancyRecord: PregnancyRecord,
  encounterDateISO: string,
): Gestation | undefined {
  if (!encounterDateISO) return undefined;
  if (shouldUsePostpartumAgeForLinkedRecord(pregnancyRecord, encounterDateISO)) {
    return undefined;
  }

  const edd = resolvePregnancyRecordEdd(pregnancyRecord);
  if (!edd) return undefined;

  const gestationAtEncounter = calculateGestationalAgeInWeeksAndDays(edd, encounterDateISO);
  return gestationAtEncounter.weeks < 0 ? undefined : gestationAtEncounter;
}

interface UseEncounterGestationalAgeResult {
  gestationalAge: Gestation | undefined;
  /**
   * Postpartum age at the encounter date.
   *
   * Set when the encounter is unlinked and the most recent past pregnancy record
   * applies, or when the encounter is linked to a retired pregnancy whose
   * delivery anchor (deliveryDate ?? endDateTime) predates the encounter. The
   * delivery anchor is always preferred over the administrative close-out date so
   * that GTPALDrawer delivery-date changes are reflected immediately.
   */
  postpartumAge: Gestation | undefined;
  pregnancyRecord: PregnancyRecord | undefined;
  /** `true` when the linked `PregnancyRecord` is active; `false` otherwise. */
  isActive: boolean;
}

/**
 * Returns the gestational age (for encounters linked to a pregnancy record) or
 * postpartum age (for unlinked encounters, derived from the most recent past
 * pregnancy record's delivery anchor on the pregnancy slice).
 *
 * When the encounter is not on any pregnancy `List.entry`, falls back to matching
 * a pregnancy by service date (`selectPregnancyRecordForEncounterDate`) so prenatal
 * Weeks survive close-out when the server link was never persisted.
 *
 * Requires pregnancy records to have been loaded into Redux before use —
 * `ClientContainer` dispatches `fetchAndDispatchPregnancyRecords` on patient load.
 *
 * @param encounter - FHIR `Encounter` resource
 * @returns Gestational age, postpartum age, the matched `PregnancyRecord`, and active flag
 */
export function useEncounterGestationalAge(encounter: Encounter): UseEncounterGestationalAgeResult {
  const patientId = resolveId(encounter.subject) ?? "";
  const encounterId = encounter.id ?? "";
  const encounterDate = encounter.period?.start ?? "";

  const pregnancyRecord = useSelector(selectPregnancyRecordForEncounter(patientId, encounterId));
  const allPregnancyRecords = useSelector(selectAllPatientPregnancyRecords(patientId));
  const mostRecentPastRecord = useSelector(
    selectMostRecentPastPregnancyRecordForEncounter(patientId, encounterDate),
  );

  const dateMatchedRecord = useMemo(
    () => selectPregnancyRecordForEncounterDate(allPregnancyRecords, encounterDate),
    [allPregnancyRecords, encounterDate],
  );

  const pregnancyRecordForPostpartum = useMemo(() => {
    if (pregnancyRecord && shouldUsePostpartumAgeForLinkedRecord(pregnancyRecord, encounterDate)) {
      return pregnancyRecord;
    }
    if (!pregnancyRecord) {
      if (
        dateMatchedRecord &&
        shouldUsePostpartumAgeForLinkedRecord(dateMatchedRecord, encounterDate)
      ) {
        return dateMatchedRecord;
      }
      if (mostRecentPastRecord) return mostRecentPastRecord;
    }
    return undefined;
  }, [pregnancyRecord, dateMatchedRecord, mostRecentPastRecord, encounterDate]);

  const gestationalAge = useMemo(() => {
    if (pregnancyRecord) {
      return gestationalAgeForPregnancyRecord(pregnancyRecord, encounterDate);
    }
    if (
      dateMatchedRecord &&
      !shouldUsePostpartumAgeForLinkedRecord(dateMatchedRecord, encounterDate)
    ) {
      return gestationalAgeForPregnancyRecord(dateMatchedRecord, encounterDate);
    }
    return undefined;
  }, [pregnancyRecord, dateMatchedRecord, encounterDate]);

  const isActive = pregnancyRecord?.isActive ?? false;

  const postpartumAge = useMemo(() => {
    if (!encounterDate || !pregnancyRecordForPostpartum) return undefined;

    const birthDateISO =
      pregnancyRecordForPostpartum.deliveryDate ?? pregnancyRecordForPostpartum.endDateTime;
    if (!birthDateISO) return undefined;

    return postpartumWeeksAndDaysAt(birthDateISO, encounterDate);
  }, [encounterDate, pregnancyRecordForPostpartum]);

  return { gestationalAge, postpartumAge, pregnancyRecord, isActive };
}
